export enum ActivityType {
  Restaurants = 'restaurants',
  Parks = 'parks',
  Golf = 'golf',
  Skiing = 'skiing',
  Fishing = 'fishing',
  AmusementParks = 'amusement-parks',
  Museums = 'museums',
  Lodging = 'lodging',
  NationalAndStateParks = 'national-and-state-parks'
}


export class ActivityFilter {
  /**
   * Unique key that identifies the activity type
   */
  key: ActivityType;

  /**
   * Label to use when displaying this activity type to the user.
   */
  label: string;

  /**
   * Place type that identifies this activity in the Google Places API. This should only be populated if the activity
   * type is one of the officially supported place types in Google Maps; otherwise, keyword should be used instead.
   * Using placeType should result in returning more relevant results, but not all activity types have a supported
   * place type.
   *
   * Refer to the following for a list of supported place types in Google Places:
   * https://developers.google.com/places/supported_types
   */
  placeType: string;

  /**
   * Keyword to use when searching for this activity in Google Maps. This should only be used if the activity type
   * does not have a supported place type in Google Places, since a keyword search is bound to return less relevant
   * results than a place type search.
   *
   * Refer to the following for a list of supported place types in Google Places:
   * https://developers.google.com/places/supported_types
   */
  keyword: string;

  /**
   * Icon to use for displaying this activity type in the UI. This property will be included in the body of <mat-icon>,
   * and should only be used for ligature-based icons.
   */
  icon: string;

  /**
   * SVG icon to use for displaying this activity type in the UI. This is mutually exclusive with the fontSet and
   * fontIcon properties.
   */
  svgIcon: string;

  /**
   * Font set to use for displaying this activity type in the UI. This should be used together with fontIcon, and is
   * mutually exclusive with svgIcon.
   */
  fontSet: string;

  /**
   * Font icon to use for displaying this activity type in the UI. This should be used together with fontSet, and is
   * mutually exclusive with svgIcon.
   */
  fontIcon: string;
}


/**
 * Array of all supported activity filters.
 */
export const ACTIVITY_FILTER_LIST: Array<ActivityFilter> = [
  {
    key: ActivityType.Restaurants,
    label: 'Restaurants',
    placeType: 'restaurant',
    keyword: null,
    icon: null,
    svgIcon: 'activities:restaurant',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Parks,
    label: 'Outdoor',
    placeType: 'park',
    keyword: null,
    icon: null,
    svgIcon: 'activities:parks',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Golf,
    label: 'Golf',
    placeType: null,
    keyword: 'golf',
    icon: null,
    svgIcon: 'activities:golf',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Skiing,
    label: 'Skiing',
    placeType: null,
    keyword: 'skiing',
    icon: null,
    svgIcon: 'activities:skiing',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Fishing,
    label: 'Fishing',
    placeType: null,
    keyword: 'fishing',
    icon: null,
    svgIcon: 'activities:fishing',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.AmusementParks,
    label: 'Amusement Parks',
    placeType: 'amusement_park',
    keyword: null,
    icon: null,
    svgIcon: 'activities:amusement_park',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Museums,
    label: 'Museums',
    placeType: 'museum',
    keyword: null,
    icon: null,
    svgIcon: 'activities:museums',
    fontSet: null,
    fontIcon: null
  },
  {
    key: ActivityType.Lodging,
    label: 'Lodging',
    placeType: 'lodging',
    keyword: null,
    icon: null,
    svgIcon: null,
    fontSet: 'fa',
    fontIcon: 'fa-bed'
  },
  {
    key: ActivityType.NationalAndStateParks,
    label: 'National and State Parks',
    placeType: null,
    keyword: 'national and state parks',
    icon: null,
    svgIcon: null,
    fontSet: 'fa',
    fontIcon: 'fa-tree'
  }
];


/**
 * Mapping from activity filter keys to their corresponding activity filters
 */
export const ACTIVITY_FILTERS: Map<ActivityType, ActivityFilter> = new Map<ActivityType, ActivityFilter>();

ACTIVITY_FILTER_LIST.forEach(activity => {
  ACTIVITY_FILTERS.set(activity.key, activity);
});
